import { supabase, toAppError } from './supabase';

/**
 * APARÊNCIA.
 *
 * Duas coisas que parecem a mesma e têm donos diferentes:
 *
 *   TEMA  — preferência de cada pessoa. Escreve no próprio perfil, e a
 *           policy de `profiles` (`id = auth.uid()`) garante que ninguém
 *           pinta o painel de outro.
 *   MARCA — logo da plataforma, global. Só quem é dono do software
 *           escreve; a policy usa `app.is_platform_admin()`.
 *
 * O front NÃO decide quem pode o quê. Ele esconde o que não interessa, e
 * o banco recusa o resto — esconder botão é conforto, não segurança.
 */

export interface UiTheme {
  code: string;
  name: string;
  accent: string;
  surface: string;
  isDark: boolean;
}

export interface PlatformBranding {
  appName: string | null;
  logoUrl: string | null;
  loginLogoUrl: string | null;
  faviconUrl: string | null;
}

export async function listThemes(): Promise<UiTheme[]> {
  const { data, error } = await supabase
    .from('ui_themes')
    .select('code, name, accent, surface, is_dark')
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw toAppError(error, 'Não foi possível carregar os temas.');

  return (data ?? []).map((linha) => ({
    code: linha.code,
    name: linha.name,
    accent: linha.accent,
    surface: linha.surface,
    isDark: linha.is_dark,
  }));
}

export async function saveTheme(userId: string, themeCode: string): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({ theme_code: themeCode })
    .eq('id', userId);

  if (error) throw toAppError(error, 'Não foi possível salvar o tema.');
}

export async function loadBranding(): Promise<PlatformBranding> {
  const { data, error } = await supabase
    .from('platform_branding')
    .select('app_name, logo_url, login_logo_url, favicon_url')
    .eq('id', 1)
    .maybeSingle();

  if (error) throw toAppError(error, 'Não foi possível carregar a marca.');

  return {
    appName: data?.app_name ?? null,
    logoUrl: data?.logo_url ?? null,
    loginLogoUrl: data?.login_logo_url ?? null,
    faviconUrl: data?.favicon_url ?? null,
  };
}

export type BrandingSlot = 'logo' | 'login_logo' | 'favicon';

/**
 * Monta o patch com a coluna certa, explicitamente.
 *
 * Uma chave dinâmica (`{ [coluna]: url }`) compila, mas apaga a checagem
 * de tipo do PostgREST — justamente no ponto que ESCREVE no banco. Três
 * linhas de `if` valem mais que a elegância de um índice computado.
 */
function patchDoSlot(slot: BrandingSlot, url: string | null) {
  const carimbo = new Date().toISOString();
  if (slot === 'logo') return { logo_url: url, updated_at: carimbo };
  if (slot === 'login_logo') return { login_logo_url: url, updated_at: carimbo };
  return { favicon_url: url, updated_at: carimbo };
}

/**
 * Envia um logo e aponta a marca para ele.
 *
 * O nome do arquivo carrega um carimbo de tempo porque o bucket é público
 * e CDN guarda por URL: sobrescrever o mesmo caminho deixaria o logo
 * antigo aparecendo por horas.
 *
 * Se a atualização da tabela falhar, o arquivo enviado fica órfão — e é
 * o custo certo. O contrário (apontar para um arquivo que não subiu)
 * quebraria a tela de login de todo mundo.
 */
export async function uploadBrandingAsset(slot: BrandingSlot, file: File): Promise<string> {
  const extensao = file.name.split('.').pop()?.toLowerCase() ?? 'png';
  const caminho = `${slot}-${Date.now()}.${extensao}`;

  const { error: erroUpload } = await supabase.storage
    .from('platform-branding')
    .upload(caminho, file, { contentType: file.type, upsert: false });

  if (erroUpload) throw toAppError(erroUpload, 'Não foi possível enviar o arquivo.');

  const { data: publico } = supabase.storage.from('platform-branding').getPublicUrl(caminho);

  const { error } = await supabase
    .from('platform_branding')
    .update(patchDoSlot(slot, publico.publicUrl))
    .eq('id', 1);

  if (error) throw toAppError(error, 'Não foi possível salvar a marca.');

  return publico.publicUrl;
}

export async function clearBrandingAsset(slot: BrandingSlot): Promise<void> {
  const { error } = await supabase
    .from('platform_branding')
    .update(patchDoSlot(slot, null))
    .eq('id', 1);

  if (error) throw toAppError(error, 'Não foi possível remover.');
}

export async function saveAppName(appName: string): Promise<void> {
  const { error } = await supabase
    .from('platform_branding')
    .update({ app_name: appName.trim() || null, updated_at: new Date().toISOString() })
    .eq('id', 1);

  if (error) throw toAppError(error, 'Não foi possível salvar o nome.');
}

/**
 * Aplica o tema nas variáveis CSS da página.
 *
 * O valor vem do banco, onde uma constraint garante que é hex de 6
 * dígitos. Sem ela, uma string arbitrária aqui viraria injeção no `style`
 * do documento — e o catálogo não é escrevível pelo cliente justamente
 * para essa string nunca vir de fora.
 */
export function applyTheme(theme: Pick<UiTheme, 'accent' | 'surface'>): void {
  const raiz = document.documentElement;
  raiz.style.setProperty('--c-primary', theme.accent);
  raiz.style.setProperty('--c-primary-soft', theme.surface);
  raiz.style.setProperty('--c-sidebar-accent', theme.accent);
}
